import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, useTheme, AppBar, Toolbar, Container, Avatar, Tooltip, Menu, MenuItem, Tabs, Tab } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import TranslateIcon from '@mui/icons-material/Translate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AppFooter from '../components/Layout/AppFooter';
import LoginModal from '../components/Common/LoginModal';
import InviteCodeModal from '../components/Common/InviteCodeModal';
import AnnouncementBanner, { checkShouldShowAnnouncement } from '../components/Common/AnnouncementBanner';
import { logout } from '../store/actions/authActions';
import { checkInviteEligibility } from '../store/actions/inviteActions';

const Landing = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAuthenticated, authChecked } = useSelector((state) => state.auth);
  const { eligible } = useSelector((state) => state.invite);

  // 滚动状态
  const [scrollY, setScrollY] = useState(0);
  const [showNavBackground, setShowNavBackground] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // 登录弹窗状态
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 邀请码弹窗状态
  const [showInviteModal, setShowInviteModal] = useState(false);

  // 鼠标移动监听（光晕跟随效果）
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // 进入动画状态
  const [titleVisible, setTitleVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  // 公告栏可见状态 - 初始值从 localStorage 检查
  const [bannerVisible, setBannerVisible] = useState(checkShouldShowAnnouncement);

  // 导航菜单项
  const navigationItems = [
    { label: '首页', path: '/landing' },
    // { label: '证件翻译', path: '/trans' },
    { label: '翻译认证', path: '/certification' },
    { label: '服务范围', path: '/services' },
    { label: '充值', path: '/pricing' },
    { label: '安全', path: '/security' }
  ];

  // 检查邀请码资格
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(checkInviteEligibility()).catch(error => {
        //console.error('检查邀请码资格失败:', error);
      });
    }
  }, [isAuthenticated, user, dispatch]);

  // 功能特性数据
  const features = [
    {
      icon: <TranslateIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '专业翻译',
      description: '支持图片、PDF、Word等多种文档格式的专业翻译服务，确保翻译质量和准确性'
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '便捷上传',
      description: '支持拖拽上传，批量处理，让文档翻译变得简单高效'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '安全保障',
      description: '企业级安全保护，确保您的文档信息安全可靠'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '人工审核',
      description: '智能翻译引擎，人工专业翻译审核，确保翻译质量和准确性'
    }
  ];

  // 滚动监听
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setShowNavBackground(currentScrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 鼠标光晕跟随效果
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // 进入动画初始化
  useEffect(() => {
    const titleTimer = setTimeout(() => {
      setTitleVisible(true);
    }, 200);

    const cardTimer = setTimeout(() => {
      setCardVisible(true);
    }, 600);

    return () => {
      clearTimeout(titleTimer);
      clearTimeout(cardTimer);
    };
  }, []);

  // 导航栏相关函数
  const getCurrentTab = () => {
    const currentPath = location.pathname;
    const index = navigationItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  };

  const handleTabChange = (event, newValue) => {
    navigate(navigationItems[newValue].path);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleCloseMenu();
    navigate('/profile');
  };

  // 处理填写邀请码
  const handleInviteCode = () => {
    handleCloseMenu();
    setShowInviteModal(true);
  };

  const handleLogout = () => {
    handleCloseMenu();
    dispatch(logout());
    navigate('/landing');
  };

  // 处理登录按钮点击
  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  // 处理登录成功
  const handleLoginSuccess = (userData) => {
    //console.log('登录成功，用户数据:', userData);
    setShowLoginModal(false);
    // 跳转到工作台页面
    navigate('/workspace');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 处理邀请码验证成功
  const handleInviteSuccess = (result) => {
    //console.log('邀请码验证成功:', result);
    // 可以在这里显示成功通知或刷新用户信息
  };

  // 计算导航元素透明度（除了Logo）
  const getNavElementOpacity = () => {
    return 1;
  };

  const navElementOpacity = getNavElementOpacity();

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* 升级公告横幅 */}
      <AnnouncementBanner
        onVisibilityChange={(visible) => setBannerVisible(visible)}
      />

      {/* 动态样式 */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
            50% { transform: translateY(-100px) rotate(180deg); opacity: 1; }
          }
          
          @keyframes scan {
            0% { transform: translateY(-10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(310px); opacity: 0; }
          }
          
          @keyframes stampDrop {
            0% { transform: rotate(-25deg) scale(2.5); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: rotate(-15deg) scale(1); opacity: 1; }
          }
          
          @keyframes bounceArrow {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0) translateX(-50%); }
            40% { transform: translateY(-15px) translateX(-50%); }
            60% { transform: translateY(-7px) translateX(-50%); }
          }
        `}
      </style>

      {/* 自定义导航栏 */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: showNavBackground
            ? 'rgba(10, 20, 40, 0.8)'
            : 'transparent',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          borderBottom: 'none',
          py: 0.5,
          padding: 0,
          boxShadow: showNavBackground
            ? '0 4px 30px rgba(0, 0, 0, 0.1)'
            : 'none',
          color: 'white',
          top: bannerVisible ? '44px' : 0
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => {
                navigate('/landing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {/* Logo */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: 'white',
                  transition: 'opacity 0.3s ease',
                  '&:hover': { opacity: 0.8 }
                }}
              >
                PageSmooth
              </Typography>
            </Box>

            {/* 右侧区域：导航菜单 + 用户信息 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* 导航菜单 */}
              <Tabs
                value={getCurrentTab()}
                onChange={handleTabChange}
                sx={{
                  opacity: navElementOpacity,
                  transition: 'opacity 0.3s ease',
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                    height: 2,
                    borderRadius: '5px 5px 0 0',
                    boxShadow: '0 2px 8px rgba(255, 255, 255, 0.4)',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.75)',
                    minWidth: 80,
                    minHeight: 64,
                    padding: '12px 12px',
                    transition: 'all 0.3s ease',
                    '&.Mui-selected': {
                      color: 'rgba(255, 255, 255, 1)',
                      fontWeight: 600
                    },
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 1)',
                      opacity: 1
                    }
                  }
                }}
              >
                {navigationItems.map((item, index) => (
                  <Tab
                    key={index}
                    label={item.label}
                    sx={{ mx: 0.5 }}
                  />
                ))}
              </Tabs>

              {/* 用户信息 - 认证状态确定前使用固定宽度占位 */}
              {!authChecked ? (
                <Box sx={{ paddingLeft: '20px', minWidth: '180px' }} />
              ) : isAuthenticated ? (
                <Box sx={{ display: 'flex', alignItems: 'center', opacity: navElementOpacity, transition: 'opacity 0.3s ease', paddingLeft: '20px' }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      navigate('/workspace');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      borderRadius: 3,
                      mr: 2,
                      backgroundColor: showNavBackground
                        ? theme.palette.primary.main
                        : 'rgba(255, 255, 255, 0.15)',
                      color: showNavBackground
                        ? 'white'
                        : 'rgba(255, 255, 255, 0.9)',
                      boxShadow: showNavBackground
                        ? '0 4px 12px rgba(67, 83, 255, 0.3)'
                        : '0 4px 12px rgba(255, 255, 255, 0.1)',
                      border: showNavBackground
                        ? '1px solid transparent'
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: showNavBackground
                          ? theme.palette.primary.main
                          : 'rgba(255, 255, 255, 0.25)',
                        boxShadow: showNavBackground
                          ? '0 6px 16px rgba(67, 83, 255, 0.4)'
                          : '0 6px 16px rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    翻译工作台
                  </Button>

                  <Tooltip title="账户设置">
                    <Button
                      onClick={handleOpenMenu}
                      endIcon={<ArrowDropDownIcon sx={{
                        color: showNavBackground
                          ? theme.palette.primary.main
                          : 'rgba(255, 255, 255, 0.9)',
                        transition: 'color 0.3s ease'
                      }} />}
                      sx={{
                        textTransform: 'none',
                        color: showNavBackground
                          ? theme.palette.primary.main
                          : 'rgba(255, 255, 255, 0.9)',
                        transition: 'color 0.3s ease',
                        '&:hover': {
                          backgroundColor: showNavBackground
                            ? theme.palette.action.hover
                            : 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          alt={user.username}
                          src={user.avatar || ''}
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: showNavBackground
                              ? theme.palette.primary.main
                              : 'rgba(255, 255, 255, 0.15)',
                            color: 'white',
                            transition: 'background-color 0.3s ease'
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            display: { xs: 'none', md: 'block' },
                            color: showNavBackground
                              ? theme.palette.primary.main
                              : 'rgba(255, 255, 255, 0.9)',
                            transition: 'color 0.3s ease'
                          }}
                        >
                          {user.username}
                        </Typography>
                      </Box>
                    </Button>
                  </Tooltip>

                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    PaperProps={{
                      elevation: 2,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                        mt: 1.5,
                        borderRadius: 2,
                        minWidth: 180,
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          mx: 1,
                          my: 0.5,
                          '&:hover': {
                            backgroundColor: theme.palette.background.light,
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleProfile}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      个人中心
                    </MenuItem>
                    {eligible && (
                      <MenuItem onClick={handleInviteCode}>
                        <CardGiftcardIcon sx={{ mr: 1, fontSize: 20 }} />
                        填写邀请码
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                      退出登录
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box sx={{ paddingLeft: '20px' }}>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleLoginClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.75,
                      borderRadius: 3,
                      // 在Landing页面默认使用hover状态的样式
                      backgroundColor: showNavBackground
                        ? theme.palette.primary.main
                        : 'rgba(255, 255, 255, 0.08)',
                      color: showNavBackground
                        ? 'white'
                        : 'rgba(255, 255, 255, 0.9)',
                      boxShadow: showNavBackground
                        ? '0 4px 12px rgba(67, 83, 255, 0.3)'
                        : '0 4px 12px rgba(255, 255, 255, 0.1)',
                      border: showNavBackground
                        ? '1px solid transparent'
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: showNavBackground
                          ? theme.palette.primary.main
                          : 'rgba(255, 255, 255, 0.15)',
                        boxShadow: showNavBackground
                          ? '0 6px 16px rgba(67, 83, 255, 0.4)'
                          : '0 6px 16px rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    立即登录体验
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 首屏 - 纯净光晕与毛玻璃卡片区域 */}
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #0a1428 0%, #1a2744 50%, #0a1428 100%)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: bannerVisible ? '44px' : 0,
          transition: 'padding-top 0.3s ease',
          justifyContent: 'center'
        }}
      >
        {/* 动态聚光灯背景 */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(83, 100, 255, 0.35), transparent 70%)`,
            transition: 'background 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 0,
            pointerEvents: 'none'
          }}
        />

        {/* 细微网格背景层 */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `
              linear-gradient(rgba(107, 120, 255, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(107, 120, 255, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            zIndex: 0,
            pointerEvents: 'none',
            maskImage: 'linear-gradient(180deg, black 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(180deg, black 40%, transparent 100%)',
          }}
        />

        {/* 主内容容器 */}
        <Box
          sx={{
            zIndex: 10,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '1200px',
            mt: 10
          }}
        >
          {/* 标题区域 */}
          <Box
            sx={{
              transform: titleVisible ? 'translateY(0)' : 'translateY(40px)',
              opacity: titleVisible ? 1 : 0,
              transition: 'all 1s cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', mb: 2 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', md: '5rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.01em',
                  background: 'linear-gradient(135deg, #4353ff, #6b78ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                一站式证件翻译平台
              </Typography>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '-60px',
                  px: 1.5,
                  py: 0.25,
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}
              >
                5.0
              </Box>
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.4rem' },
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 400,
                letterSpacing: '0.02em',
                mb: 6
              }}
            >
              签证、出海、移民、NAATI、翻译认证全覆盖
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/workspace');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  setShowLoginModal(true);
                }
              }}
              sx={{
                color: '#fff',
                background: 'linear-gradient(135deg, #4353ff, #6b78ff)',
                border: '1px solid rgba(107, 120, 255, 0.5)',
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '100px',
                textTransform: 'none',
                boxShadow: '0 8px 32px rgba(67, 83, 255, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5363ff, #7b88ff)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(67, 83, 255, 0.4)'
                }
              }}
            >
              开始体验
            </Button>
          </Box>

          {/* 核心视觉展示：Glassmorphism 平行翻译卡片 */}
          <Box
            sx={{
              mt: 10,
              width: '100%',
              maxWidth: '900px',
              height: '350px',
              position: 'relative',
              transform: cardVisible ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.95)',
              opacity: cardVisible ? 1 : 0,
              transition: 'all 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 24px 64px -12px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* 原文侧 */}
              <Box sx={{ flex: 1, p: 4, position: 'relative', textAlign: 'left' }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em', mb: 4 }}>
                  原始文件
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ height: '12px', width: '40%', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '80%', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '65%', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '90%', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '75%', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }} />
                </Box>
              </Box>

              {/* 分割线与扫描动效中心钮 */}
              <Box sx={{ width: '1px', background: 'linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.15), transparent)', position: 'relative' }}>
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#000000',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                }}>
                  ⇄
                </Box>
              </Box>

              {/* 翻译侧 */}
              <Box sx={{ flex: 1, p: 4, position: 'relative', textAlign: 'left', background: 'rgba(255, 255, 255, 0.02)' }}>
                <Typography sx={{ color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em', mb: 4 }}>
                  翻译结果
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, position: 'relative', zIndex: 2 }}>
                  <Box sx={{ height: '12px', width: '40%', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '80%', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '65%', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '90%', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '4px' }} />
                  <Box sx={{ height: '12px', width: '75%', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '4px' }} />
                </Box>
                {/* 扫描线光效 */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(180deg, transparent 0%, rgba(67, 83, 255, 0.15) 50%, transparent 100%)',
                    animation: 'scan 3s infinite linear',
                    zIndex: 1
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 底部引导箭头 */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'bounceArrow 2s infinite',
            zIndex: 10,
            color: 'white',
            cursor: 'pointer',
            opacity: 0.6,
            transition: 'opacity 0.3s ease',
            '&:hover': {
              opacity: 1
            }
          }}
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M12 20L5 13M12 20L19 13" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
      </Box>

      {/* 第二屏及后续内容 */}
      <Box sx={{
        py: 4,
        backgroundColor: 'rgba(228, 228, 228, 0.6)',
        minHeight: '100vh'
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '40px',
              boxShadow: 'none',
              overflow: 'hidden',
              p: { xs: 2, sm: 3 },
              height: '100%'
            }}
          >
            {/* 欢迎区域 */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(90deg, #4353FF 0%, #6B78FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 2,
                  mt: 8
                }}
              >
                欢迎使用 PageSmooth
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 12, maxWidth: 600, mx: 'auto' }}
              >
                提供像素级还原、高质量、认证级、安全的证件翻译服务
              </Typography>
            </Box>

            {/* 支持的文档类型 */}
            <Box sx={{ mb: 8 }}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: 'center',
                  mb: 8,
                  mt: 8,
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                支持文档
              </Typography>

              <Grid container spacing={3}>
                {/* 个人证件 */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                        border: '2px solid rgba(67, 83, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      个人证件
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {['身份证', '出生证明', '工作证', '驾驶证', '行驶证', '车辆登记证书', '退休证'].map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 500
                          }}
                        >
                          {doc}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                {/* 家庭证件 */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                        border: '2px solid rgba(67, 83, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FamilyRestroomIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      家庭证件
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {['结婚证', '离婚证', '户口本', '房产证'].map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 500
                          }}
                        >
                          {doc}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                {/* 学历证书 */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                        border: '2px solid rgba(67, 83, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SchoolIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      学历证书
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {['毕业证', '学位证', '成绩单', '学生证', '教师资格证'].map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 500
                          }}
                        >
                          {doc}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                {/* 企业证件 */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                        border: '2px solid rgba(67, 83, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <BusinessIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      企业证件
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {['营业执照', '税务登记证', '商标证书', '开户许可证'].map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 500
                          }}
                        >
                          {doc}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                {/* 流水清单 */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                        border: '2px solid rgba(67, 83, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <ReceiptLongIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      流水清单
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {['银行流水账单', '个人所得税纳税清单'].map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 500
                          }}
                        >
                          {doc}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                {/* 其它证明 */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                        border: '2px solid rgba(67, 83, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <DescriptionIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      其它证明
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {['在读证明', '实习证明', '在职证明', '工作证明', '离职证明'].map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: 500
                          }}
                        >
                          {doc}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* 翻译效果 */}
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                mb: 8,
                mt: 24,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              翻译效果
            </Typography>

            <Grid container spacing={6}>
              {/* 第一组对比：营业执照 */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 35px rgba(67, 83, 255, 0.15)'
                    }
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: 'center',
                        mb: 3,
                        fontWeight: 600,
                        color: theme.palette.primary.main
                      }}
                    >
                      营业执照翻译
                    </Typography>

                    {/* 翻译前 */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '220px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '16px',
                          border: '1px dashed #ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 翻译前标签 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderTopLeftRadius: '16px',
                            borderBottomRightRadius: '16px',
                            zIndex: 2
                          }}
                        >
                          翻译前
                        </Box>
                        <img
                          src="/images/yingye3.jpg"
                          alt="中文营业执照原件"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* 箭头 */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          color: 'white'
                        }}
                      >
                        ↓
                      </Box>
                    </Box>

                    {/* 翻译后 */}
                    <Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: '220px',
                          backgroundColor: '#f8f9ff',
                          borderRadius: '16px',
                          border: `1px solid ${theme.palette.primary.main}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 翻译后标签 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderTopLeftRadius: '12px',
                            borderBottomRightRadius: '16px',
                            zIndex: 2
                          }}
                        >
                          翻译后
                        </Box>
                        <img
                          src="/images/yingye4.jpg"
                          alt="英文营业执照翻译件"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* 第二组对比：成绩单 */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 35px rgba(67, 83, 255, 0.15)'
                    }
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: 'center',
                        mb: 3,
                        fontWeight: 600,
                        color: theme.palette.primary.main
                      }}
                    >
                      成绩单翻译
                    </Typography>

                    {/* 翻译前 */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '220px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '16px',
                          border: '1px dashed #ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 翻译前标签 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderTopLeftRadius: '12px',
                            borderBottomRightRadius: '16px',
                            zIndex: 2
                          }}
                        >
                          翻译前
                        </Box>
                        <img
                          src="/images/score3.jpg"
                          alt="中文成绩单原件"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* 箭头 */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          color: 'white'
                        }}
                      >
                        ↓
                      </Box>
                    </Box>

                    {/* 翻译后 */}
                    <Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: '220px',
                          backgroundColor: '#f8f9ff',
                          borderRadius: '16px',
                          border: `1px solid ${theme.palette.primary.main}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 翻译后标签 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderTopLeftRadius: '12px',
                            borderBottomRightRadius: '16px',
                            zIndex: 2
                          }}
                        >
                          翻译后
                        </Box>
                        <img
                          src="/images/score4.jpg"
                          alt="英文成绩单翻译件"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* 第三组对比：银行流水 */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 35px rgba(67, 83, 255, 0.15)'
                    }
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: 'center',
                        mb: 3,
                        fontWeight: 600,
                        color: theme.palette.primary.main
                      }}
                    >
                      银行流水翻译
                    </Typography>

                    {/* 翻译前 */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '220px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '16px',
                          border: '1px dashed #ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 翻译前标签 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderTopLeftRadius: '12px',
                            borderBottomRightRadius: '16px',
                            zIndex: 2
                          }}
                        >
                          翻译前
                        </Box>
                        <img
                          src="/images/bank3.jpg"
                          alt="中文银行流水原件"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* 箭头 */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          color: 'white'
                        }}
                      >
                        ↓
                      </Box>
                    </Box>

                    {/* 翻译后 */}
                    <Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: '220px',
                          backgroundColor: '#f8f9ff',
                          borderRadius: '16px',
                          border: `1px solid ${theme.palette.primary.main}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 翻译后标签 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderTopLeftRadius: '12px',
                            borderBottomRightRadius: '16px',
                            zIndex: 2
                          }}
                        >
                          翻译后
                        </Box>
                        <img
                          src="/images/bank4.jpg"
                          alt="英文银行流水翻译件"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* 功能特性 */}
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                mb: 8,
                mt: 24,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              功能特性
            </Typography>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* 他们都在用 - 用户评价轮播 */}
            <Box sx={{ mt: 24, mb: 8 }}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: 'center',
                  mb: 6,
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                他们都在用
              </Typography>

              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  height: '320px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover .testimonial-track': {
                    animationPlayState: 'paused'
                  }
                }}
              >
                <Box
                  className="testimonial-track"
                  sx={{
                    display: 'flex',
                    gap: 3,
                    animation: 'scroll 45s linear infinite',
                    width: 'fit-content',
                    '@keyframes scroll': {
                      '0%': { transform: 'translateX(0)' },
                      '100%': { transform: 'translateX(-50%)' }
                    }
                  }}
                >
                  {/* 第一组评价卡片 */}
                  {[
                    {
                      type: '留学机构',
                      name: '启德教育',
                      title: '专业留学咨询机构',
                      content: '我们为学生处理各种留学文件翻译，PageSmooth的翻译质量非常专业，格式保持完美，大大提高了我们的工作效率。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '张同学',
                      title: '澳洲留学生',
                      content: '申请签证需要翻译很多材料，包括还需要NAATI认证，PageSmooth帮我完成了成绩单和毕业证的翻译，翻译很赞！',
                      rating: 5
                    },
                    {
                      type: '淘宝商家',
                      name: '海外代购小店',
                      title: '跨境电商',
                      content: '经常需要翻译产品说明书和海关文件，PageSmooth让我们的业务更加顺畅，客户满意度大幅提升。',
                      rating: 5
                    },
                    {
                      type: '留学机构',
                      name: '起航远途',
                      title: '澳洲留学服务机构',
                      content: '作为留学服务机构，我们对翻译质量要求比较高。PageSmooth的认证翻译服务完全满足我们的需求，服务一对一很很赞。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '李女士',
                      title: '移民申请人',
                      content: '移民材料翻译要求很严格，PageSmooth提供的认证翻译让我顺利通过了审核，非常感谢！',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '王先生',
                      title: '企业高管',
                      content: '出国工作需要翻译各种证件，PageSmooth的服务快速准确，让我的签证申请过程很顺利。',
                      rating: 5
                    },
                    {
                      type: '淘宝商家',
                      name: '全球购精品店',
                      title: '进口商品零售',
                      content: '进口商品的各种证书翻译都交给PageSmooth，专业可靠，是我们信赖的合作伙伴。',
                      rating: 5
                    },
                    {
                      type: '留学机构',
                      name: '留学小站',
                      title: '留学服务专家',
                      content: '我们推荐PageSmooth给所有需要文件翻译的学生，质量有保障，价格也很合理。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '陈博士',
                      title: '海外学者',
                      content: '学术论文和研究资料的翻译对准确性要求极高，PageSmooth从未让我失望过。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '刘小姐',
                      title: '旅游爱好者',
                      content: '出国旅游前翻译了驾照和各种证件，PageSmooth让我的旅行更加安心。',
                      rating: 4
                    }
                  ].concat([
                    // 重复一遍数据以实现无缝循环
                    {
                      type: '翻译机构',
                      name: '译悦翻译',
                      title: '专业翻译机构',
                      content: '我们为学生处理各种留学文件翻译，PageSmooth的翻译质量非常专业，格式保持完美，大大提高了我们的工作效率。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '杨同学',
                      title: '澳洲留学生',
                      content: '申请签证需要翻译很多材料，PageSmooth帮我快速完成了成绩单和毕业证的翻译，翻译质量很棒！',
                      rating: 5
                    },
                    {
                      type: '淘宝商家',
                      name: '海外代购小店',
                      title: '跨境电商',
                      content: '经常需要翻译产品说明书和海关文件，PageSmooth让我们的业务更加顺畅，客户满意度大幅提升。',
                      rating: 5
                    },
                    {
                      type: '留学机构',
                      name: '远洋留学',
                      title: '留学教育机构',
                      content: 'PageSmooth的认证翻译服务完全满足我们的需求，服务很专业。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '王女士',
                      title: '移民申请人',
                      content: '移民材料翻译要求很严格，PageSmooth提供的认证翻译让我顺利通过了审核，非常感谢！',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '王先生',
                      title: '企业高管',
                      content: '出国工作需要翻译各种证件，PageSmooth的服务快速准确，让我的签证申请过程很顺利。',
                      rating: 5
                    },
                    {
                      type: '淘宝商家',
                      name: '全球购精品店',
                      title: '进口商品零售',
                      content: '进口商品的各种证书翻译都交给PageSmooth，专业可靠，是我们信赖的合作伙伴。',
                      rating: 5
                    },
                    {
                      type: '留学机构',
                      name: '津桥留学',
                      title: '留学服务专家',
                      content: '我们推荐PageSmooth给所有需要文件翻译的学生，质量有保障，价格也很合理。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '陈博士',
                      title: '海外学者',
                      content: '学术论文和研究资料的翻译对准确性要求极高，PageSmooth从未让我失望过。',
                      rating: 5
                    },
                    {
                      type: '个人',
                      name: '刘小姐',
                      title: '旅游爱好者',
                      content: '出国旅游前翻译了驾照和各种证件，PageSmooth让我的旅行更加安心。',
                      rating: 4
                    }
                  ]).map((testimonial, index) => (
                    <Card
                      key={index}
                      sx={{
                        minWidth: '350px',
                        maxWidth: '350px',
                        p: 3,
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 30px rgba(67, 83, 255, 0.15)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {/* 头像占位符 */}
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                              fill="rgba(0, 0, 0, 0.3)"
                            />
                          </svg>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {testimonial.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem'
                              }}
                            >
                              {testimonial.title}
                            </Typography>
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                backgroundColor: testimonial.type === '留学机构'
                                  ? 'rgba(67, 83, 255, 0.1)'
                                  : testimonial.type === '淘宝商家'
                                    ? 'rgba(255, 152, 0, 0.1)'
                                    : 'rgba(76, 175, 80, 0.1)',
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                color: testimonial.type === '留学机构'
                                  ? theme.palette.primary.main
                                  : testimonial.type === '淘宝商家'
                                    ? '#ff9800'
                                    : '#4caf50',
                                fontWeight: 500
                              }}
                            >
                              {testimonial.type}
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.6,
                          color: 'text.secondary',
                          mb: 2,
                          fontSize: '0.875rem'
                        }}
                      >
                        "{testimonial.content}"
                      </Typography>

                      {/* 星级评分 */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {[...Array(5)].map((_, starIndex) => (
                          <Box
                            key={starIndex}
                            sx={{
                              color: starIndex < testimonial.rating
                                ? '#ffc107'
                                : 'rgba(0, 0, 0, 0.1)',
                              fontSize: '1rem',
                              mr: 0.25
                            }}
                          >
                            ★
                          </Box>
                        ))}
                        <Typography
                          variant="caption"
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        >
                          {testimonial.rating}/5
                        </Typography>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* 快速操作区域 */}
            <Box sx={{ mt: 24, mb: 8, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ mb: 8, fontWeight: 600, color: theme.palette.primary.main }}>
                快速开始
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)'
                      }
                    }}
                    onClick={() => {
                      navigate('/workspace');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, color: theme.palette.primary.main }}>
                      上传文档
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      立即上传您的文档开始翻译
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)'
                      }
                    }}
                    onClick={() => {
                      navigate('/pricing');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, color: theme.palette.primary.main }}>
                      查看定价
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      了解我们的服务套餐和价格
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 底部Footer */}
      <AppFooter />

      {/* Login Modal */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Invite Code Modal */}
      <InviteCodeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={handleInviteSuccess}
      />
    </Box>
  );
};

export default Landing; 